import {
  patientAccessModes,
  tenantRoleKeys,
  type PatientAccessMode,
  type TenantRoleKey,
} from "@amplecare/identity-model";

export type { PatientAccessMode, TenantRoleKey } from "@amplecare/identity-model";

export const tenantSessionBootstrapModes = [
  "bearer-token",
  "cookie-session",
  "scaffold-header",
] as const;

export type TenantSessionBootstrapMode =
  (typeof tenantSessionBootstrapModes)[number];

export const tenantSessionPreviewBootstrapContractVersion =
  "tenant-session-preview-bootstrap-v1" as const;

export const tenantSessionPreviewBootstrapRoutePath =
  "/api/v1/tenant-session/bootstrap-preview" as const;

export const workforceSessionLoginContractVersion =
  "workforce-session-login-v1" as const;

export const workforceSessionLoginRoutePath =
  "/api/v1/workforce-session/login" as const;

export const workforceSessionCurrentContractVersion =
  "workforce-session-current-v1" as const;

export const workforceSessionCurrentRoutePath =
  "/api/v1/workforce-session/current" as const;

export const workforceSessionLogoutContractVersion =
  "workforce-session-logout-v1" as const;

export const workforceSessionLogoutRoutePath =
  "/api/v1/workforce-session/logout" as const;

export const workforceSessionSubjectsContractVersion =
  "workforce-session-subjects-v1" as const;

export const workforceSessionSubjectsRoutePath =
  "/api/v1/workforce-session/subjects" as const;

export const workforceSessionAuthorizeContractVersion =
  "workforce-session-authorize-v1" as const;

export const workforceSessionAuthorizeRoutePath =
  "/api/v1/workforce-session/authorize" as const;

export const workforceSessionAccessTokenContractVersion =
  "workforce-session-access-token-v1" as const;

export const workforceSessionAccessTokenRoutePath =
  "/api/v1/workforce-session/access-token" as const;

export const tenantSessionPreviewLoginContractVersion =
  "tenant-session-preview-login-v1" as const;

export const tenantSessionPreviewLoginRoutePath =
  "/api/v1/tenant-session/login-preview" as const;

export const tenantSessionAuthorizePreviewContractVersion =
  "tenant-session-authorize-preview-v1" as const;

export const tenantSessionAuthorizePreviewRoutePath =
  "/api/v1/tenant-session/authorize-preview" as const;

export const tenantSessionPreviewSubjectsContractVersion =
  "tenant-session-preview-subjects-v1" as const;

export const tenantSessionPreviewSubjectsRoutePath =
  "/api/v1/tenant-session/preview-subjects" as const;

export const tenantSessionAccessTokenContractVersion =
  "tenant-session-access-token-v1" as const;

export const tenantSessionAccessTokenRoutePath =
  "/api/v1/tenant-session/access-token" as const;

export const tenantSessionAccessTokenGrantTypes = [
  "authorization_code",
  "preview_subject",
] as const;

export type TenantSessionAccessTokenGrantType =
  (typeof tenantSessionAccessTokenGrantTypes)[number];

export const tenantSessionCodeChallengeMethod = "S256" as const;
export type TenantSessionCodeChallengeMethod =
  typeof tenantSessionCodeChallengeMethod;

export const tenantSessionBootstrapHeaderNames = {
  bootstrapMode: "x-amplecare-session-bootstrap",
  roleKeys: "x-amplecare-session-role-keys",
  patientAccessMode: "x-amplecare-session-patient-access-mode",
  siteId: "x-amplecare-session-site-id",
} as const;

export interface TenantSessionScaffoldHeaderInput {
  bootstrapMode: TenantSessionBootstrapMode;
  roleKeys?: readonly TenantRoleKey[];
  patientAccessMode?: PatientAccessMode | null;
  siteId?: string | null;
}

export interface TenantSessionPreviewBootstrapRequestV1 {
  schemaVersion: typeof tenantSessionPreviewBootstrapContractVersion;
  tenantId: string;
  siteId?: string | null;
  clinicianUserId: string;
  roleKeys?: readonly TenantRoleKey[];
  patientAccessMode?: PatientAccessMode | null;
}

export interface TenantSessionPreviewBootstrapResponseV1 {
  schemaVersion: typeof tenantSessionPreviewBootstrapContractVersion;
  bootstrapMode: "cookie-session";
  session: {
    actorId: string;
    tenantId: string;
    siteId: string | null;
    roleKeys: TenantRoleKey[];
    patientAccessMode: PatientAccessMode;
    expiresAt: string;
  };
}

export interface WorkforceSessionSummaryV1 {
  actorId: string;
  tenantId: string;
  siteId: string | null;
  roleKeys: TenantRoleKey[];
  patientAccessMode: PatientAccessMode;
  subjectKey: string;
  displayName: string | null;
  clinicianUserId: string;
  patientId: string | null;
  expiresAt: string;
}

export interface WorkforceSessionLoginRequestV1 {
  schemaVersion: typeof workforceSessionLoginContractVersion;
  tenantId: string;
  subjectKey?: string | null;
  email?: string | null;
  password?: string | null;
}

export interface WorkforceSessionLoginResponseV1 {
  schemaVersion: typeof workforceSessionLoginContractVersion;
  sessionMode: "browser-session";
  session: WorkforceSessionSummaryV1;
}

export interface WorkforceSessionCurrentResponseV1 {
  schemaVersion: typeof workforceSessionCurrentContractVersion;
  sessionMode: "browser-session";
  session: WorkforceSessionSummaryV1;
}

export interface WorkforceSessionLogoutResponseV1 {
  schemaVersion: typeof workforceSessionLogoutContractVersion;
  sessionMode: "browser-session";
  signedOut: true;
}

export interface WorkforceSessionSubjectSummaryV1 {
  subjectKey: string;
  displayName: string | null;
  loginEmail?: string | null;
  siteId: string | null;
  clinicianUserId: string;
  patientId: string | null;
  roleKeys: TenantRoleKey[];
  patientAccessMode: PatientAccessMode;
  isAutoSelected: boolean;
  requiresPassword: boolean;
}

export interface WorkforceSessionSubjectsResponseV1 {
  schemaVersion: typeof workforceSessionSubjectsContractVersion;
  tenantId: string;
  subjects: WorkforceSessionSubjectSummaryV1[];
}

export interface WorkforceSessionAuthorizeRequestV1 {
  schemaVersion: typeof workforceSessionAuthorizeContractVersion;
  tenantId?: TenantSessionPreviewSubjectLocator["tenantId"];
  subjectKey?: TenantSessionPreviewSubjectLocator["subjectKey"];
  siteId?: TenantSessionPreviewSubjectLocator["siteId"];
  clinicianUserId?: TenantSessionPreviewSubjectLocator["clinicianUserId"];
  codeChallenge: string;
  codeChallengeMethod: TenantSessionCodeChallengeMethod;
}

export interface WorkforceSessionAuthorizeResponseV1 {
  schemaVersion: typeof workforceSessionAuthorizeContractVersion;
  bootstrapMode: "bearer-token";
  grantType: "authorization_code";
  authorizationCode: string;
  expiresAt: string;
  codeChallengeMethod: TenantSessionCodeChallengeMethod;
}

export interface WorkforceSessionAccessTokenAuthorizationCodeRequestV1 {
  schemaVersion: typeof workforceSessionAccessTokenContractVersion;
  grantType: "authorization_code";
  authorizationCode: string;
  codeVerifier: string;
}

export type WorkforceSessionAccessTokenRequestV1 =
  WorkforceSessionAccessTokenAuthorizationCodeRequestV1;

export interface WorkforceSessionAccessTokenResponseV1 {
  schemaVersion: typeof workforceSessionAccessTokenContractVersion;
  bootstrapMode: "bearer-token";
  tokenType: "Bearer";
  accessToken: string;
  session: {
    actorId: string;
    tenantId: string;
    siteId: string | null;
    roleKeys: TenantRoleKey[];
    patientAccessMode: PatientAccessMode;
    expiresAt: string;
  };
}

export interface TenantSessionPreviewLoginRequestV1 {
  schemaVersion: typeof tenantSessionPreviewLoginContractVersion;
  tenantId: string;
  subjectKey?: string | null;
  previewProfileKey?: string | null;
  email?: string | null;
  password?: string | null;
}

export interface TenantSessionPreviewLoginResponseV1 {
  schemaVersion: typeof tenantSessionPreviewLoginContractVersion;
  loginMode: "browser-session";
  subject: {
    tenantId: string;
    subjectKey: string;
    previewProfileKey: string;
    displayName: string | null;
    siteId: string | null;
    clinicianUserId: string;
    patientId: string | null;
    expiresAt: string;
  };
}

export interface TenantSessionPreviewSubjectLocator {
  tenantId: string;
  subjectKey?: string | null;
  previewProfileKey?: string | null;
  siteId?: string | null;
  clinicianUserId?: string | null;
}

export interface TenantSessionAuthorizePreviewRequestV1 {
  schemaVersion: typeof tenantSessionAuthorizePreviewContractVersion;
  tenantId?: TenantSessionPreviewSubjectLocator["tenantId"];
  subjectKey?: TenantSessionPreviewSubjectLocator["subjectKey"];
  previewProfileKey?: TenantSessionPreviewSubjectLocator["previewProfileKey"];
  siteId?: TenantSessionPreviewSubjectLocator["siteId"];
  clinicianUserId?: TenantSessionPreviewSubjectLocator["clinicianUserId"];
  codeChallenge: string;
  codeChallengeMethod: TenantSessionCodeChallengeMethod;
}

export interface TenantSessionAuthorizePreviewResponseV1 {
  schemaVersion: typeof tenantSessionAuthorizePreviewContractVersion;
  bootstrapMode: "bearer-token";
  grantType: "authorization_code";
  authorizationCode: string;
  expiresAt: string;
  codeChallengeMethod: TenantSessionCodeChallengeMethod;
}

export interface TenantSessionPreviewSubjectSummaryV1 {
  subjectKey: string;
  previewProfileKey: string;
  displayName: string | null;
  loginEmail?: string | null;
  siteId: string | null;
  clinicianUserId: string;
  patientId: string | null;
  roleKeys: TenantRoleKey[];
  patientAccessMode: PatientAccessMode;
  isAutoSelected: boolean;
  requiresPassword: boolean;
}

export interface TenantSessionPreviewSubjectsResponseV1 {
  schemaVersion: typeof tenantSessionPreviewSubjectsContractVersion;
  tenantId: string;
  subjects: TenantSessionPreviewSubjectSummaryV1[];
}

export interface TenantSessionAccessTokenPreviewSubjectRequestV1 {
  schemaVersion: typeof tenantSessionAccessTokenContractVersion;
  grantType?: "preview_subject";
  tenantId: TenantSessionPreviewSubjectLocator["tenantId"];
  subjectKey?: TenantSessionPreviewSubjectLocator["subjectKey"];
  previewProfileKey?: TenantSessionPreviewSubjectLocator["previewProfileKey"];
  siteId?: TenantSessionPreviewSubjectLocator["siteId"];
  clinicianUserId?: TenantSessionPreviewSubjectLocator["clinicianUserId"];
}

export interface TenantSessionAccessTokenAuthorizationCodeRequestV1 {
  schemaVersion: typeof tenantSessionAccessTokenContractVersion;
  grantType: "authorization_code";
  authorizationCode: string;
  codeVerifier: string;
}

export type TenantSessionAccessTokenRequestV1 =
  | TenantSessionAccessTokenPreviewSubjectRequestV1
  | TenantSessionAccessTokenAuthorizationCodeRequestV1;

export interface TenantSessionAccessTokenResponseV1 {
  schemaVersion: typeof tenantSessionAccessTokenContractVersion;
  bootstrapMode: "bearer-token";
  tokenType: "Bearer";
  accessToken: string;
  session: {
    actorId: string;
    tenantId: string;
    siteId: string | null;
    roleKeys: TenantRoleKey[];
    patientAccessMode: PatientAccessMode;
    expiresAt: string;
  };
}

function uniqueRoleKeys(roleKeys: readonly TenantRoleKey[]): TenantRoleKey[] {
  return [...new Set(roleKeys)];
}

export function resolveTenantSessionBootstrapMode(
  value: string | null | undefined,
): TenantSessionBootstrapMode {
  if (value === "scaffold-header") {
    return "scaffold-header";
  }

  if (value === "bearer-token") {
    return "bearer-token";
  }

  return "cookie-session";
}

export function readTenantSessionBootstrapHeaderValue(
  headers: Record<string, string | null | undefined>,
  headerName: string,
): string | null {
  const normalizedHeaderName = headerName.toLowerCase();

  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() !== normalizedHeaderName) {
      continue;
    }

    return typeof value === "string" && value.length > 0 ? value : null;
  }

  return null;
}

export function resolveTenantSessionRoleKeys(
  value: string | null | undefined,
): TenantRoleKey[] {
  if (value == null || value.length === 0) {
    return ["clinician"];
  }

  const roleKeys = value
    .split(",")
    .map((entry) => entry.trim())
    .filter(
      (entry): entry is TenantRoleKey =>
        tenantRoleKeys.includes(entry as TenantRoleKey),
    );

  return roleKeys.length > 0 ? uniqueRoleKeys(roleKeys) : ["clinician"];
}

export function resolveTenantSessionPatientAccessMode(
  value: string | null | undefined,
): PatientAccessMode {
  return patientAccessModes.includes(value as PatientAccessMode)
    ? (value as PatientAccessMode)
    : "tenant-panel";
}

export function buildTenantSessionScaffoldHeaders(
  input: TenantSessionScaffoldHeaderInput,
): Record<string, string> {
  if (input.bootstrapMode !== "scaffold-header") {
    return {};
  }

  const headers: Record<string, string> = {
    [tenantSessionBootstrapHeaderNames.bootstrapMode]: "scaffold-header",
    [tenantSessionBootstrapHeaderNames.roleKeys]: uniqueRoleKeys(
      input.roleKeys ?? ["clinician"],
    ).join(","),
    [tenantSessionBootstrapHeaderNames.patientAccessMode]:
      input.patientAccessMode ?? "tenant-panel",
  };

  if (input.siteId) {
    headers[tenantSessionBootstrapHeaderNames.siteId] = input.siteId;
  }

  return headers;
}
